import { UserAccountType } from "@common/constants";
import { SubmitFilter } from "@data-types/account-statement/SubmitFilter";
import { TransactionTableResponse } from "@data-types/account-statement/TransactionTableResponse";
import { UserActivityTypes } from "@data-types/account-statement/UserActivityTypes";
import {
	AccountStatementService,
	CUSTOM_PERIOD,
	TPeriod,
	TransactionLookup,
	rawFilter,
} from "@services/account-statement/AccountStatementService";
import {
	addToUrlParams,
	getUrlParamsAsObj,
	history,
	localizationService,
	setUrlParams,
} from "@state";
import { LoaderStore } from "@state/stores/common";
import { ACCOUNT_STATEMENT_DEFAULT_FILTER } from "@utils/account-statement/AccountStatementUtils";
import { buildBettingAccountTypeString } from "@utils/common";
import {
	getWallet,
	mapBettingTypeAbrvToId,
} from "@utils/common/bettingAccountUtils";
import { safeJsonParse } from "@utils/format/formatter";
import { StorageStateKeysEnum } from "@utils/storage";
import { DateTime } from "luxon";
import { action, computed, observable, runInAction } from "mobx";

import RootStore from "../RootStore";
import AccountStatementDetailsStore from "./AccountStatementDetailsStore";

export default class AccountStatementStore {
	rootStore: RootStore;
	loader: LoaderStore;
	transactionTableLoader: LoaderStore;
	accountStatementDetailsStore: AccountStatementDetailsStore;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;

		this.loader = new LoaderStore();
		this.transactionTableLoader = new LoaderStore();
		this.accountStatementDetailsStore = new AccountStatementDetailsStore(
			this
		);
	}

	//#region observables and consts

	@observable tableData: TransactionTableResponse | null = null;
	@observable bettingAndPaymentTableData: UserActivityTypes | null = null;

	@observable showCustomDateSection: boolean = false;
	@observable transactionLookup: TransactionLookup | null = null;
	lastSelectedPeriod: TPeriod = null;
	@observable statementFilter: rawFilter | null = null;
	@observable isStoreInitialized: boolean = false;
	@observable isMessageInitialized: boolean = false;
	lookupCache = new Map();

	//#endregion observables and consts

	@computed get shouldDisplayInitialLoader() {
		return !this.isStoreInitialized;
	}

	@computed get shouldShowSkeletonLoader() {
		return this.loader.isLoading;
	}

	@computed get shouldShowSkeletonTransactionLoader() {
		return this.transactionTableLoader.isLoading;
	}

	//#region setters
	@action.bound
	setStatementFilter(filter: rawFilter) {
		this.statementFilter = filter;
	}

	@action.bound
	setLastSelectedPeriod(period: TPeriod) {
		this.lastSelectedPeriod = period;
	}

	@action.bound
	setShowCustomDateSection(isShown: boolean) {
		this.showCustomDateSection = isShown;
	}

	@action.bound
	setMessageInitialized(value: boolean) {
		this.isMessageInitialized = value;
	}

	//#endregion setters

	@action.bound
	async onInitialize() {
		this.loader.suspend();

		this.setInitialFilter();

		this.isStoreInitialized = true;

		if (!this.statementFilter) {
			console.error("statement filter is not set");
			return;
		}
		await this.getTransactionLookup({
			shopId: this.statementFilter.shopId,
			bettingAccountTypeAbrv: this.statementFilter.bettingAccountTypeAbrv,
		});

		await Promise.allSettled([
			this.fetchBettingAndPaymentTableData(this.statementFilter),
			this.fetchTransactionsTable(this.statementFilter?.pageNumber || 1),
		]);
		this.loader.resume();
	}

	@action.bound
	setInitialFilter() {
		let data: rawFilter | null = null;
		if (history.location.search) {
			data = this.getUrlData();
			this.setStatementFilter(data);
			if (this.statementFilter?.asPeriod == CUSTOM_PERIOD) {
				this.setShowCustomDateSection(true);
			}
		} else {
			let bettingAccountTypeAbrv = null;
			let shopId = null;
			const preservedFilter = this.getFilterFromStorage();

			if (
				preservedFilter &&
				this.rootStore.userAuthStore.availableWallets?.some(
					(w) =>
						w.abrv == preservedFilter.bettingAccountTypeAbrv &&
						w.shopId == preservedFilter.shopId
				)
			) {
				bettingAccountTypeAbrv = preservedFilter.bettingAccountTypeAbrv;
				shopId = preservedFilter.shopId;
			} else {
				const firstOnlineWallet =
					this.rootStore.userAuthStore.onlineUserWallets.find(
						(wallet) =>
							wallet.shopId ==
								this.rootStore.userAuthStore
									.defaultOnlineShopId &&
							(wallet.abrv ===
								UserAccountType.SportBettingAccountOnline ||
								wallet.abrv === UserAccountType.CasinoAccount ||
								wallet.abrv ===
									UserAccountType.LiveCasinoAccount ||
								wallet.abrv === UserAccountType.Games)
					);

				bettingAccountTypeAbrv = firstOnlineWallet?.abrv;
				shopId = firstOnlineWallet?.shopId;
			}

			if (!bettingAccountTypeAbrv) {
				// Must exists if online user does not exist
				const shop = this.rootStore.userAuthStore.shopUserShops.get(
					this.rootStore.userAuthStore.activeShopUserShopId || ""
				);
				shopId = shop?.shopId;
				bettingAccountTypeAbrv = shop?.wallets[0].abrv;
			}
			if (!bettingAccountTypeAbrv || !shopId) {
				console.error(
					" missing bettingAccountTypeAbrv or shopId unable to create filter",
					{ bettingAccountTypeAbrv, shopId }
				);
				return;
			}
			data = {
				...ACCOUNT_STATEMENT_DEFAULT_FILTER,
				bettingAccountTypeAbrv: bettingAccountTypeAbrv,
				shopId: shopId,
			};
			this.setStatementFilter(data);
		}
		this?.setLastSelectedPeriod(
			this.statementFilter?.asPeriod ||
				ACCOUNT_STATEMENT_DEFAULT_FILTER.asPeriod
		);
	}

	@action.bound
	async getTransactionLookup(filter: {
		shopId?: string;
		bettingAccountTypeAbrv?: string;
	}) {
		const wallet = getWallet(
			filter?.shopId,
			filter?.bettingAccountTypeAbrv,
			this.rootStore.userAuthStore.availableWallets
		);
		if (!wallet) {
			console.error("Error: Unable to find wallet", {
				shopId: filter?.shopId,
				accountTypeAbrv: filter?.bettingAccountTypeAbrv,
			});
			return;
		}
		const cachedLookups = this.lookupCache.get(wallet.id);
		if (cachedLookups) {
			runInAction(() => {
				this.transactionLookup = cachedLookups;
			});
			return;
		}
		try {
			const data =
				await AccountStatementService.getTransactionLookup(wallet);

			runInAction(() => {
				if (data) {
					this.transactionLookup = data;
					this.lookupCache.set(wallet.id, data);
				}
			});
		} catch (error) {
			console.error(error);
			this.rootStore.notificationStore.error(
				localizationService.t("ACC_STATEMENT.FILTER." + error?.message)
			);
		}
	}

	@action.bound
	async onFilterSubmit(formData: rawFilter) {
		this.loader.suspend();
		if (formData.asPeriod != CUSTOM_PERIOD) {
			delete formData.startDate;
			delete formData.endDate;
		}

		if (!formData.bettingAccountTypeAbrv) {
			formData.bettingAccountTypeAbrv =
				this.rootStore.userAuthStore.availableUserWalletsIds![0];
		}
		await this.getTransactionLookup({
			shopId: formData.shopId,
			bettingAccountTypeAbrv: formData.bettingAccountTypeAbrv,
		});
		if (formData.asType != "all") {
			const selectedWalletTransactions = this.transactionLookup;
			if (selectedWalletTransactions) {
				const transactionType =
					selectedWalletTransactions.transactionTypes?.find(
						(transaction) => transaction.abrv == formData.asType
					);
				if (!transactionType) {
					formData.asType = "all";
				}
			}
		}
		if (formData.asPeriod === "custom") {
			if (!formData.startDate || !formData.endDate) {
				this.showCustomDateSection = false;
				formData.asPeriod = this.lastSelectedPeriod;
			} else {
				formData.startDate = DateTime.fromISO(formData.startDate)
					.startOf("day")
					.toUTC()
					.toISO();
				formData.endDate = DateTime.fromISO(formData.endDate)
					.endOf("day")
					.toUTC()
					.toISO();
			}
		}

		this.lastSelectedPeriod = formData.asPeriod;

		this.setStatementFilter(formData);
		this.saveFilterToStorage(formData);
		setUrlParams({ ...formData, pageNumber: 1 });

		this.bettingAndPaymentTableData = null;
		this.tableData = null;
		await Promise.allSettled([
			this.fetchBettingAndPaymentTableData(formData),
			this.fetchTransactionsTable(this.statementFilter?.pageNumber),
		]);

		this.loader.resume();
	}

	@action.bound
	async fetchBettingAndPaymentTableData(filter: rawFilter) {
		if (filter == null) {
			return;
		}
		try {
			const submitFilter = { ...filter };
			/* This has to be done like this since on twdk there is chance that retail and online shop have same shopId
			so it's not possible to just use map to find valid wallet
			*/
			const selectedWallet = getWallet(
				filter.shopId,
				filter.bettingAccountTypeAbrv,
				this.rootStore.userAuthStore.availableWallets
			);

			if (!selectedWallet) {
				return null;
			}

			let activeWallet = selectedWallet;

			submitFilter.bettingAccountTypeAbrv = buildBettingAccountTypeString(
				{
					bettingAccountTypeAbrv: filter.bettingAccountTypeAbrv,
					shopId: selectedWallet.shopId,
					rootStore: this.rootStore,
				}
			);
			if (!activeWallet) {
				throw "ERROR_OCCURED";
			}
			const response =
				await AccountStatementService.getBettingAndPaymentTableData(
					submitFilter,
					activeWallet
				);
			if (response) {
				runInAction(() => {
					this.bettingAndPaymentTableData = response;
				});
			}
		} catch (error) {
			this.rootStore.notificationStore.error(
				localizationService.t(
					"ACC_STATEMENT.TABLES." + (error?.message || error)
				)
			);
		}
	}

	@action.bound
	async fetchTransactionsTable(pageNr = 1) {
		try {
			if (!this.statementFilter) {
				return;
			}

			const submitFilter: SubmitFilter = {
				...this.statementFilter,
				bettingTypeIds: [],
				activeWallet: null,
			};
			const selectedWallet = getWallet(
				submitFilter.shopId,
				submitFilter.bettingAccountTypeAbrv,
				this.rootStore.userAuthStore.availableWallets
			);
			if (!selectedWallet) {
				return null;
			}
			submitFilter.activeWallet = selectedWallet;
			const bettingTypeIds = mapBettingTypeAbrvToId({
				rootStore: this.rootStore,
				bettingAccountTypeAbrv: submitFilter.bettingAccountTypeAbrv,
				shopId: submitFilter.shopId,
			});
			if (!bettingTypeIds?.length) {
				return;
			}

			submitFilter.bettingTypeIds = bettingTypeIds;
			if (submitFilter.asType != "all") {
				submitFilter.asType =
					this.transactionLookup?.transactionTypes?.find(
						(t) => t.abrv == submitFilter.asType
					)?.id || "all";
			}
			let response =
				await AccountStatementService.fetchTransactionsTableData(
					submitFilter,
					pageNr || 1
				);
			if (response) {
				runInAction(() => {
					this.tableData = response;
				});
				addToUrlParams({
					...this.statementFilter,
					pageNumber: response.page,
				});
			}
		} catch (error) {
			this.tableData = null;
			this.rootStore.notificationStore.error(
				localizationService.t(
					"ACC_STATEMENT.TABLES." + (error?.message || error)
				)
			);
		}
	}

	@action.bound
	async changePage(pageNr = 1) {
		this.transactionTableLoader.suspend();
		await this.fetchTransactionsTable(pageNr);
		this.transactionTableLoader.resume();
	}
	getUrlData() {
		return getUrlParamsAsObj<rawFilter>();
	}

	saveFilterToStorage(filter: rawFilter) {
		if (filter.shopId == null || filter.bettingAccountTypeAbrv == null) {
			return;
		}

		const mappedFilter = {
			shopId: filter.shopId,
			bettingAccountTypeAbrv: filter.bettingAccountTypeAbrv,
		};
		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.ACCOUNT_STATEMENT_FILTER,
			JSON.stringify(mappedFilter)
		);
	}

	getFilterFromStorage() {
		const filter = safeJsonParse<{
			shopId: string;
			bettingAccountTypeAbrv: string;
		} | null>(
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.ACCOUNT_STATEMENT_FILTER
			) as string,
			null
		);

		return filter;
	}

	@action.bound
	onDispose() {
		this.transactionLookup = null;
		this.tableData = null;
		this.bettingAndPaymentTableData = null;
		this.showCustomDateSection = false;
		this.lastSelectedPeriod = null;
		this.statementFilter = null;
		this.lookupCache.clear();
	}
}

export enum DetailsTypeEnum {
	None,
	BetSlipDetails,
	TransactionDetails,
}
